import { z } from 'zod';

/**
 * Client config — the ONE typed module the rest of `apps/ui/web` reads
 * (frontend-architecture skill R14). No raw `import.meta.env.X` is permitted
 * anywhere else; every call site reads `config.*`.
 *
 * **Build-once (D4-06 / ADR-0029).** The deployed bundle must be
 * env-agnostic so one artifact promotes across dev/tst/prd, so env-specific
 * values arrive at **runtime**, not via build-time `VITE_*` inlining:
 *
 * - **Production** (`import.meta.env.PROD`) — fetch `/config.json` at boot
 *   (top-level await) and validate it with {@link RuntimeConfigSchema}. The
 *   deploy writes `config.json` to the site bucket (public Auth0/API values +
 *   the RUM block from the ObservabilityStack outputs); the bundle itself
 *   carries no env-specific values. Because Vite statically replaces
 *   `import.meta.env.PROD` with `true` in the prod build, the dev branch below
 *   (and its `import.meta.env.VITE_*` reads) is dead-code-eliminated — so no
 *   `VITE_*` value is inlined into the shipped bundle.
 * - **Local dev + test** (`!import.meta.env.PROD`) — read the `VITE_*` vars
 *   from `.env.local` / vitest's `test.env` synchronously, exactly as before.
 *   No `/config.json` is needed locally.
 *
 * Either way the resolved {@link AppConfig} has the same shape, so the five
 * consumers (`AuthProvider`, the API client, `rum`, `reportError`, the test
 * MSW handlers) and `main.tsx` are unchanged. Every value is PUBLIC by design
 * (Auth0 domain / SPA clientId / org id / API audience / RUM ids) — never put
 * a secret in `config.json` or a `VITE_` var (security SR1). The app still
 * **fails fast** with a clear error if the runtime config is missing/invalid
 * (the throw rejects the entry module graph, so React never mounts a broken
 * app — same fail-fast contract as before).
 */

/** The resolved client config — identical shape from both the runtime and env paths. */
export interface AppConfig {
  readonly auth0: {
    readonly domain: string;
    readonly clientId: string;
    readonly audience: string;
    readonly organization: string;
  };
  readonly apiBaseUrl: string;
  readonly rum?: {
    readonly appMonitorId: string;
    readonly identityPoolId: string;
    readonly guestRoleArn: string;
    readonly region: string;
  };
  /**
   * Build-mode flag (`import.meta.env.PROD`) — NOT from the runtime config.
   * `true` only in a `vite build` output. Read by `reportError`/`rum` so env
   * reads go through `config`, not raw `import.meta.env` (R14).
   */
  readonly isDeployedEnv: boolean;
}

/* ── Production: runtime `/config.json` ─────────────────────────────────── */

/**
 * Shape of the fetched `/config.json` — the clean (un-prefixed) public config.
 * `rum` is optional (omit the block to leave the RUM client a no-op).
 */
const RuntimeConfigSchema = z.object({
  auth0: z.object({
    domain: z.string().min(1, 'auth0.domain is required'),
    clientId: z.string().min(1, 'auth0.clientId is required'),
    audience: z.url('auth0.audience must be a URL'),
    organization: z
      .string()
      .regex(/^org_[A-Za-z0-9]+$/, 'auth0.organization must look like org_xxx'),
  }),
  apiBaseUrl: z.url('apiBaseUrl must be a URL'),
  rum: z
    .object({
      appMonitorId: z.string().min(1),
      identityPoolId: z.string().min(1),
      guestRoleArn: z.string().min(1),
      region: z.string().min(1),
    })
    .optional(),
});

/** Format Zod issues as an indented, multi-line `path: message` list for a fail-fast error. */
function formatZodIssues(error: z.ZodError): string {
  return error.issues.map((i) => `  - ${i.path.join('.')}: ${i.message}`).join('\n');
}

/** Validate a parsed `/config.json` object into a typed {@link AppConfig}. Throws on a bad shape. */
export function parseRuntimeConfig(json: unknown): AppConfig {
  const parsed = RuntimeConfigSchema.safeParse(json);
  if (!parsed.success) {
    throw new Error(`Invalid runtime config (/config.json):\n${formatZodIssues(parsed.error)}`);
  }
  return { ...parsed.data, isDeployedEnv: import.meta.env.PROD };
}

/** Fetch + validate `/config.json` (no-store so an env-config change is picked up immediately). */
export async function fetchRuntimeConfig(fetchImpl: typeof fetch = fetch): Promise<AppConfig> {
  let res: Response;
  try {
    res = await fetchImpl('/config.json', { cache: 'no-store' });
  } catch (cause) {
    throw new Error(`Failed to fetch /config.json (network error): ${String(cause)}`);
  }
  // NB: on the deployed edge a MISSING /config.json does not 404 — the SPA's
  // CloudFront error-mapping rewrites 403/404 → /index.html with HTTP 200
  // (StaticSite construct), so the real "config absent" signal is the
  // JSON-parse failure below, not this branch. Kept for completeness (direct
  // S3 / local-preview / a true 5xx still surface here).
  if (!res.ok) {
    throw new Error(`Failed to fetch /config.json: HTTP ${res.status} ${res.statusText}`);
  }
  let json: unknown;
  try {
    json = await res.json();
  } catch {
    throw new Error('Failed to parse /config.json as JSON (missing or not a JSON document).');
  }
  return parseRuntimeConfig(json);
}

/* ── Local dev + test: `import.meta.env` (VITE_*) ───────────────────────── */

const EnvSchema = z.object({
  VITE_AUTH0_DOMAIN: z.string().min(1, 'VITE_AUTH0_DOMAIN is required'),
  VITE_AUTH0_CLIENT_ID: z.string().min(1, 'VITE_AUTH0_CLIENT_ID is required'),
  VITE_AUTH0_AUDIENCE: z.url('VITE_AUTH0_AUDIENCE must be a URL'),
  VITE_AUTH0_ORG_ID: z
    .string()
    .regex(/^org_[A-Za-z0-9]+$/, 'VITE_AUTH0_ORG_ID must look like org_xxx'),
  VITE_API_BASE_URL: z.url('VITE_API_BASE_URL must be a URL'),
  // RUM is optional locally (rum.ts no-ops); `""` reads as absent (see normaliseEnv).
  VITE_RUM_APP_MONITOR_ID: z.string().min(1).optional(),
  VITE_RUM_IDENTITY_POOL_ID: z.string().min(1).optional(),
  VITE_RUM_GUEST_ROLE_ARN: z.string().min(1).optional(),
  VITE_RUM_REGION: z.string().min(1).optional(),
});

/** Coerce blank `""` env vars to `undefined` BEFORE Zod sees them. */
function normaliseEnv(env: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(env)) {
    out[k] = typeof v === 'string' && v === '' ? undefined : v;
  }
  return out;
}

function loadConfigFromEnv(): AppConfig {
  const parsed = EnvSchema.safeParse(normaliseEnv(import.meta.env));
  if (!parsed.success) {
    throw new Error(
      `Invalid client configuration. Check apps/ui/web/.env.local against ` +
        `.env.local.example:\n${formatZodIssues(parsed.error)}`,
    );
  }
  const env = parsed.data;
  // The four RUM vars are one feature: all four or none (a half-config crashes AwsRum).
  const rumEnabled =
    !!env.VITE_RUM_APP_MONITOR_ID &&
    !!env.VITE_RUM_IDENTITY_POOL_ID &&
    !!env.VITE_RUM_GUEST_ROLE_ARN &&
    !!env.VITE_RUM_REGION;
  return {
    auth0: {
      domain: env.VITE_AUTH0_DOMAIN,
      clientId: env.VITE_AUTH0_CLIENT_ID,
      audience: env.VITE_AUTH0_AUDIENCE,
      organization: env.VITE_AUTH0_ORG_ID,
    },
    apiBaseUrl: env.VITE_API_BASE_URL,
    rum: rumEnabled
      ? {
          appMonitorId: env.VITE_RUM_APP_MONITOR_ID!,
          identityPoolId: env.VITE_RUM_IDENTITY_POOL_ID!,
          guestRoleArn: env.VITE_RUM_GUEST_ROLE_ARN!,
          region: env.VITE_RUM_REGION!,
        }
      : undefined,
    isDeployedEnv: import.meta.env.PROD,
  };
}

/* ── Resolved config ────────────────────────────────────────────────────── */

// Top-level await: in PROD this awaits the `/config.json` fetch before any
// importer (and `main.tsx`) evaluates; in dev/test the env branch resolves
// synchronously. A rejection here fails the whole entry graph fast (React
// never mounts — the pre-render fail-fast contract, same as the old throw).
//
// Build-once guarantee (D4-06): the prod bundle carries NO env-specific
// value. Two safeguards: (1) Vite replaces `import.meta.env.PROD` with `true`,
// so the dev arm (`loadConfigFromEnv`, the only `import.meta.env` reader) is
// dead-code-eliminated; (2) the CI deploy builds with NO `.env*` present, so
// there are no `VITE_*` values to inline even if (1) regressed. Any NEW
// env-specific read MUST live inside `loadConfigFromEnv` to preserve this.
export const config: AppConfig = import.meta.env.PROD
  ? await fetchRuntimeConfig()
  : loadConfigFromEnv();
